import React, { useState, useEffect } from 'react';
import { collection, doc, addDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { sanitizeEntity, sanitizeString } from '../../utils/sanitization';
import { logEntityChanges } from '../../utils/logger';
import { useAuth } from '../auth/AuthContext';
import { generateLogId, generateFaqId } from '../../utils/documentIdUtils';
import { hasPermission, FEATURE_PERMISSIONS } from '../../utils/permissions';

/**
 * SupportPageFaqEditor - Modal form for creating/editing FAQ items
 * 
 * Features:
 * - Create new FAQ or edit existing one
 * - Section selector dropdown
 * - Dynamic steps and keywords arrays
 * - Auto-generates slug-based document IDs
 * - Auto-sync option to chatbotKnowledge
 * 
 * Props:
 * - initialFaq: Existing FAQ object (null for new FAQ)
 * - sections: Array of section objects for the dropdown
 * - onClose: Function to call when closing modal
 * - onSaved: Function to call after successful save (with success message)
 */
const SupportPageFaqEditor = ({ initialFaq = null, sections = [], onClose, onSaved }) => {
  const { currentUser } = useAuth();
  const isEditing = Boolean(initialFaq?.id);
  
  // Form state
  const [question, setQuestion] = useState(initialFaq?.question || '');
  const [answer, setAnswer] = useState(initialFaq?.answer || '');
  const [sectionId, setSectionId] = useState(initialFaq?.sectionId || '');
  const [steps, setSteps] = useState(initialFaq?.steps || ['']);
  const [keywords, setKeywords] = useState(initialFaq?.keywords || ['']);
  const [isActive, setIsActive] = useState(initialFaq?.isActive ?? true);
  const [order, setOrder] = useState(initialFaq?.order || 0);
  
  // Lifecycle state
  const [status, setStatus] = useState(initialFaq?.status || (initialFaq?.isActive === false ? 'archived' : 'published'));
  const [reviewDate, setReviewDate] = useState(initialFaq?.reviewDate || '');
  const [expiresAt, setExpiresAt] = useState(initialFaq?.expiresAt || '');
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Add new step
  const addStep = () => {
    setSteps([...steps, '']);
  };
  
  // Update step at index
  const updateStep = (index, value) => {
    const newSteps = [...steps];
    newSteps[index] = value;
    setSteps(newSteps);
  };
  
  // Remove step at index
  const removeStep = (index) => {
    setSteps(steps.filter((_, i) => i !== index));
  };
  
  // Add new keyword
  const addKeyword = () => {
    setKeywords([...keywords, '']);
  };
  
  // Update keyword at index
  const updateKeyword = (index, value) => {
    const newKeywords = [...keywords];
    newKeywords[index] = value;
    setKeywords(newKeywords);
  };
  
  // Remove keyword at index
  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };
  
  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission(currentUser, FEATURE_PERMISSIONS.KNOWLEDGE_MANAGE)) return;
    
    if (!question.trim()) {
      setError('Question is required');
      return;
    }
    
    if (!answer.trim()) {
      setError('Answer is required');
      return;
    }
    
    if (!sectionId) {
      setError('Section is required');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      // Get section details
      const selectedSection = sections.find(s => s.id === sectionId);
      if (!selectedSection) {
        throw new Error('Selected section not found');
      }
      
      // Filter out empty steps/keywords
      const cleanSteps = steps.filter(s => s.trim());
      const cleanKeywords = keywords.filter(k => k.trim());
      
      // Build FAQ data
      const derivedIsActive = status === 'published';
      
      const faqData = {
        question: sanitizeString(question, 500),
        answer: sanitizeString(answer, 2000),
        steps: cleanSteps.map(s => sanitizeString(s, 500)),
        keywords: cleanKeywords.map(k => sanitizeString(k, 100)),
        sectionId,
        sectionTitle: selectedSection.title,
        sectionIcon: selectedSection.icon,
        requiredPermission: selectedSection.requiredPermission,
        category: sectionId, // For backward compatibility
        order: parseInt(order) || 0,
        isActive: derivedIsActive,
        status,
        reviewDate: reviewDate || null,
        expiresAt: expiresAt || null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.displayName || 'Unknown'
      };
      
      // Track review if publishing
      if (status === 'published' && initialFaq?.status !== 'published') {
        faqData.lastReviewedAt = serverTimestamp();
        faqData.lastReviewedBy = currentUser?.displayName || currentUser?.email;
      }
      
      if (isEditing) {
        // Update existing FAQ
        const docRef = doc(db, collectionPath('supportPageFaqs'), initialFaq.id);
        await updateDoc(docRef, faqData);
        
        // Log changes
        await logEntityChanges({
          oldObj: initialFaq,
          newObj: { ...initialFaq, ...faqData },
          fields: ['question', 'answer', 'steps', 'keywords', 'sectionId', 'isActive'],
          entityType: 'SupportPageFaq',
          entityId: initialFaq.id,
          user: currentUser,
          action: 'Updated'
        });
        
        onSaved('FAQ updated successfully');
      } else {
        // Create new FAQ with timestamp-based ID
        const docId = generateFaqId(question);
        
        const docRef = doc(db, collectionPath('supportPageFaqs'), docId);
        await setDoc(docRef, {
          ...faqData,
          createdAt: serverTimestamp(),
          createdBy: currentUser?.displayName || 'Unknown'
        });
        
        // Log creation
        const logId = generateLogId('SupportPageFaq', docId, 'created');
        await addDoc(collection(db, collectionPath('logs')), {
          id: logId,
          timestamp: serverTimestamp(),
          level: 'info',
          category: 'entity_change',
          action: 'Created',
          entityType: 'SupportPageFaq',
          entityId: docId,
          userId: currentUser?.uid || 'system',
          userEmail: currentUser?.email || 'system',
          userName: currentUser?.displayName || 'System',
          changes: [],
          metadata: { question: faqData.question }
        });
        
        onSaved('FAQ created successfully');
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving FAQ:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit FAQ' : 'Create New FAQ'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          {/* Question */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., How do I create a new customer?"
              required
            />
          </div>
          
          {/* Answer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Answer <span className="text-red-500">*</span>
            </label>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Provide a brief answer to the question..."
              rows={4}
              required
            />
          </div>
          
          {/* Section Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section <span className="text-red-500">*</span>
            </label>
            <select
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">-- Select a section --</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>
                  {section.icon} {section.title}
                </option>
              ))}
            </select>
          </div>
          
          {/* Steps (Dynamic Array) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Steps (Optional)
            </label>
            <div className="space-y-2">
              {steps.map((step, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={step}
                    onChange={(e) => updateStep(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Step ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeStep(index)}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    title="Remove step"
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addStep}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                + Add Step
              </button>
            </div>
          </div>
          
          {/* Keywords (Dynamic Array) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keywords (Optional)
            </label>
            <div className="space-y-2">
              {keywords.map((keyword, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => updateKeyword(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Keyword ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeKeyword(index)}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    title="Remove keyword"
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addKeyword}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                + Add Keyword
              </button>
            </div>
          </div>
          
          {/* Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Order
            </label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:w-48"
              min="0"
            />
          </div>
          
          {/* Lifecycle Status */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <label className="block text-sm font-semibold text-gray-800">Lifecycle Status</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
                { value: 'published', label: 'Published', color: 'bg-green-100 text-green-700' },
                { value: 'needs-review', label: 'Needs Review', color: 'bg-amber-100 text-amber-700' },
                { value: 'archived', label: 'Archived', color: 'bg-red-100 text-red-700' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    status === opt.value
                      ? `${opt.color} border-current ring-2 ring-offset-1 ring-current`
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Next Review Date</label>
                <input
                  type="date"
                  value={reviewDate}
                  onChange={(e) => setReviewDate(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expires On <span className="text-gray-400">(optional)</span></label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {isEditing && initialFaq?.lastReviewedAt && (
              <p className="text-xs text-gray-500 pt-1">
                Last reviewed: {initialFaq.lastReviewedAt?.toDate?.()?.toLocaleDateString() || 'N/A'} by {initialFaq.lastReviewedBy || 'Unknown'}
              </p>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : (isEditing ? 'Update FAQ' : 'Create FAQ')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportPageFaqEditor;
